import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'alpantos.theme';

function readStoredTheme(): Theme | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : null;
  } catch {
    // Private windows and blocked site data throw on access; the OS preference
    // is a perfectly good fallback.
    return null;
  }
}

/**
 * Dark is the brand, so dark is the default — including when the operating
 * system says otherwise. The toggle is still there for anyone who wants light,
 * and their choice is what `readStoredTheme` returns from then on.
 */
const DEFAULT_THEME: Theme = 'dark';

/** Reads the stored choice, falls back to the brand default, and stamps <html>. */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme() ?? DEFAULT_THEME);

  useEffect(() => {
    document.documentElement.dataset['theme'] = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Preference simply does not persist; the UI still works.
    }
  }, [theme]);

  const toggle = useCallback(() => setTheme((current) => (current === 'dark' ? 'light' : 'dark')), []);

  return { theme, toggle };
}
